import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  ArrowLeft, Calendar, User, Armchair, Activity, Clock, 
  TrendingUp, Phone, PhoneOff, CheckCircle, Timer, Users, Baby, Mail, Heart, AlertTriangle, Pencil, X, Save, Smartphone, RefreshCw, Lock, ShoppingCart, ClipboardList, Receipt 
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const AllocationDetails = () => {
  const { allocationId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [allocation, setAllocation] = useState(null);
  const [seats, setSeats] = useState([]);
  const [seatTypes, setSeatTypes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [associatedGuestProfiles, setAssociatedGuestProfiles] = useState([]);
  const [editingProfile, setEditingProfile] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [changeSeatsOpen, setChangeSeatsOpen] = useState(false);
  const [selectedSeatIds, setSelectedSeatIds] = useState([]);
  const [allocatedSeats, setAllocatedSeats] = useState([]);
  const [savingSeats, setSavingSeats] = useState(false);
  const [seatConfirmOpen, setSeatConfirmOpen] = useState(false);
  const [activeCart, setActiveCart] = useState(null);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    fetchAllocationDetails();
  }, [allocationId]);

  const fetchAllocationDetails = async () => {
    try {
      setLoading(true);
      const userData = localStorage.getItem('userData');
      if (!userData) return;

      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;

      // Fetch allocation
      const allocResponse = await axios.get(`${BACKEND_URL}/api/allocations/${propertyId}`);
      if (allocResponse.data.success) {
        const foundAllocation = allocResponse.data.allocations.find(a => a.id === allocationId);
        if (foundAllocation) {
          setAllocation(foundAllocation);
          calculateAnalytics(foundAllocation);
        } else {
          toast({
            title: "Error",
            description: "Allocation not found",
            variant: "destructive"
          });
          navigate('/user/allocation');
        }
      }

      // Fetch seats
      const seatsResponse = await axios.get(`${BACKEND_URL}/api/seats/${propertyId}`);
      if (seatsResponse.data.success) {
        setSeats(seatsResponse.data.seats);
      }

      // Fetch seat types
      const seatTypesResponse = await axios.get(`${BACKEND_URL}/api/seat-types/${propertyId}`);
      if (seatTypesResponse.data.success) {
        setSeatTypes(seatTypesResponse.data.seatTypes);
      }

      // Fetch staff
      const staffResponse = await axios.get(`${BACKEND_URL}/api/staff/${propertyId}`);
      if (staffResponse.data.success) {
        setStaff(staffResponse.data.staff);
      }

      // Fetch devices
      const devicesResponse = await axios.get(`${BACKEND_URL}/api/devices/${propertyId}`);
      if (devicesResponse.data.success) {
        setDevices(devicesResponse.data.devices);
      }
    } catch (error) {
      console.error('Error fetching allocation details:', error);
      toast({
        title: "Error",
        description: "Failed to load allocation details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch allocated seats when opening change seats modal
  const fetchAllocatedSeats = async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) return;
      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;
      const currentDate = new Date().toISOString().split('T')[0];
      
      const response = await axios.get(
        `${BACKEND_URL}/api/allocations/${propertyId}/allocated-seats?date=${currentDate}`
      );
      if (response.data.success) {
        // Exclude current allocation's seats from the allocated list
        const otherAllocatedSeats = response.data.allocatedSeatIds.filter(
          seatId => !allocation.seatIds.includes(seatId)
        );
        setAllocatedSeats(otherAllocatedSeats);
      }
    } catch (error) {
      console.error('Error fetching allocated seats:', error);
    }
  };

  const handleOpenChangeSeats = () => {
    setSelectedSeatIds([...allocation.seatIds]);
    fetchAllocatedSeats();
    setChangeSeatsOpen(true);
  };

  const handleSeatToggle = (seatId) => {
    setSelectedSeatIds(prev => {
      if (prev.includes(seatId)) {
        return prev.filter(id => id !== seatId);
      } else {
        return [...prev, seatId];
      }
    });
  };

  const handleSaveSeats = async () => {
    if (selectedSeatIds.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one seat",
        variant: "destructive"
      });
      return;
    }

    try {
      setSavingSeats(true);
      const response = await axios.put(`${BACKEND_URL}/api/allocations/${allocationId}`, {
        seatIds: selectedSeatIds
      });

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Seats updated successfully"
        });
        setChangeSeatsOpen(false);
        fetchAllocationDetails();
      }
    } catch (error) {
      console.error('Error updating seats:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update seats",
        variant: "destructive"
      });
    } finally {
      setSavingSeats(false);
    }
  };

  // Fetch associated guest profiles when allocation is loaded
  useEffect(() => {
    if (allocation) {
      fetchAssociatedGuestProfiles();
    }
  }, [allocation]);

  const fetchAssociatedGuestProfiles = async () => {
    if (!allocation) return;
    
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) return;
      
      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;
      
      // Fetch associated guest profiles by room number
      const response = await axios.get(
        `${BACKEND_URL}/api/guests/by-room/${propertyId}/${allocation.roomNumber}/associated-guest-profiles`
      );
      
      if (response.data.success) {
        setAssociatedGuestProfiles(response.data.guestProfiles);
      }
    } catch (error) {
      console.error('Error fetching associated guest profiles:', error);
    }
  };

  const fetchCartAndOrders = async () => {
    if (!allocation) return;
    try {
      const [cartRes, ordersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/allocations/${allocation.id}/cart`),
        axios.get(`${BACKEND_URL}/api/allocations/${allocation.id}/orders`)
      ]);
      if (cartRes.data.code === 200) {
        setActiveCart(cartRes.data.data);
      }
      if (ordersRes.data.code === 200) {
        setOrders(ordersRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching cart/orders:', error);
    }
  };

  useEffect(() => {
    if (allocation) {
      fetchCartAndOrders();
    }
  }, [allocation]);

  const openEditModal = (profile) => {
    setEditingProfile(profile);
    setEditFormData({
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      countryCode: profile.countryCode || '',
      phoneNumber: profile.phoneNumber || '',
      dateOfBirth: profile.dateOfBirth || '',
      nationality: profile.nationality || '',
      allergies: profile.allergies || '',
      preferences: profile.preferences || '',
      favouriteDrink: profile.favouriteDrink || '',
      favouriteFood: profile.favouriteFood || '',
      numVisits: profile.numVisits || 0
    });
  };

  const closeEditModal = () => {
    setEditingProfile(null);
    setEditFormData({});
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveGuestProfile = async () => {
    if (!editingProfile) return;

    try {
      setSavingProfile(true);
      
      // Get user info from localStorage
      const userData = localStorage.getItem('userData');
      const user = userData ? JSON.parse(userData) : {};
      
      const payload = {
        ...editFormData,
        updatedByName: user.name || 'Unknown User',
        updatedByEmail: user.email || 'unknown@email.com'
      };
      
      const response = await axios.put(
        `${BACKEND_URL}/api/guest-profiles/${editingProfile.id}`,
        payload
      );

      if (response.data.success) {
        toast({
          title: "Success",
          description: "Guest profile updated successfully"
        });
        
        // Update the local state
        setAssociatedGuestProfiles(prev => 
          prev.map(p => p.id === editingProfile.id ? { ...p, ...editFormData } : p)
        );
        
        closeEditModal();
      }
    } catch (error) {
      console.error('Error updating guest profile:', error);
      toast({
        title: "Error",
        description: "Failed to update guest profile",
        variant: "destructive"
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const calculateAnalytics = (alloc) => {
    const events = alloc.events || [];
    const createdAt = new Date(alloc.createdAt);
    const now = new Date();
    
    // Total allocation time (from creation to now or completion)
    const completedEvent = events.find(e => e.eventType === "Status Change" && e.newValue === "Complete");
    const endTime = completedEvent ? new Date(completedEvent.timestamp) : now;
    const totalTimeMs = endTime - createdAt;
    const totalTimeSeconds = Math.floor(totalTimeMs / 1000);
    const totalTimeHours = Math.floor(totalTimeSeconds / 3600);
    const totalTimeMinutes = Math.floor((totalTimeSeconds % 3600) / 60);
    const totalTimeRemainderSeconds = totalTimeSeconds % 60;
    
    // Count calling events
    const callingOnEvents = events.filter(e => e.eventType === "Calling On");
    const callingOffEvents = events.filter(e => e.eventType === "Calling Off");
    const callingCount = callingOnEvents.length;
    
    // Calculate average calling time
    let totalCallingTime = 0;
    let callingDurations = [];
    
    for (let i = 0; i < callingOnEvents.length; i++) {
      const onEvent = callingOnEvents[i];
      const onTime = new Date(onEvent.timestamp);
      
      // Find the next calling off event after this on event
      const offEvent = callingOffEvents.find(off => new Date(off.timestamp) > onTime);
      
      if (offEvent) {
        const offTime = new Date(offEvent.timestamp);
        const duration = offTime - onTime;
        callingDurations.push(duration);
        totalCallingTime += duration;
      } else if (alloc.callingFlag !== "Non Calling") {
        // Still calling, calculate duration till now
        const duration = now - onTime;
        callingDurations.push(duration);
        totalCallingTime += duration;
      }
    }
    
    const avgCallingTimeSeconds = callingCount > 0 ? Math.floor(totalCallingTime / callingCount / 1000) : 0;
    const avgCallingTimeMinutes = Math.floor(avgCallingTimeSeconds / 60);
    const avgCallingTimeRemainderSeconds = avgCallingTimeSeconds % 60;
    
    const totalCallingSeconds = Math.floor(totalCallingTime / 1000);
    const totalCallingMinutes = Math.floor(totalCallingSeconds / 60);
    const totalCallingRemainderSeconds = totalCallingSeconds % 60;
    
    // Calculate active time (time in "Active" status)
    const activeStartEvent = events.find(e => e.eventType === "Status Change" && e.newValue === "Active");
    let activeTimeSeconds = 0;
    
    if (activeStartEvent) {
      const activeStartTime = new Date(activeStartEvent.timestamp);
      
      // Find when it left Active status
      const activeEndEvent = events.find(e => 
        e.eventType === "Status Change" && 
        e.oldValue === "Active" &&
        new Date(e.timestamp) > activeStartTime
      );
      
      const activeEndTime = activeEndEvent ? new Date(activeEndEvent.timestamp) : 
        (alloc.status === "Active" ? now : activeStartTime);
      
      activeTimeSeconds = Math.floor((activeEndTime - activeStartTime) / 1000);
    }
    
    const activeTimeMinutes = Math.floor(activeTimeSeconds / 60);
    const activeTimeRemainderSeconds = activeTimeSeconds % 60;
    
    // Status change count
    const statusChangeCount = events.filter(e => e.eventType === "Status Change").length;
    
    setAnalytics({
      totalTime: { hours: totalTimeHours, minutes: totalTimeMinutes, seconds: totalTimeRemainderSeconds },
      callingCount,
      avgCallingTime: { minutes: avgCallingTimeMinutes, seconds: avgCallingTimeRemainderSeconds },
      totalCallingTime: { minutes: totalCallingMinutes, seconds: totalCallingRemainderSeconds },
      activeTime: { minutes: activeTimeMinutes, seconds: activeTimeRemainderSeconds },
      statusChangeCount,
      callingDurations
    });
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case "Created":
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      case "Status Change":
        return <Activity className="w-5 h-5 text-purple-500" />;
      case "Calling On":
        return <Phone className="w-5 h-5 text-red-500" />;
      case "Calling Off":
        return <PhoneOff className="w-5 h-5 text-green-500" />;
      default:
        return <Activity className="w-5 h-5 text-slate-500" />;
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Allocated': 'bg-blue-100 text-blue-700 border-blue-300',
      'Active': 'bg-green-100 text-green-700 border-green-300',
      'Billing': 'bg-purple-100 text-purple-700 border-purple-300',
      'Clear': 'bg-teal-100 text-teal-700 border-teal-300',
      'Complete': 'bg-slate-100 text-slate-700 border-slate-300'
    };
    return colors[status] || 'bg-slate-100 text-slate-700 border-slate-300';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getSeatNumbers = () => {
    if (!allocation || !seats.length) return [];
    return allocation.seatIds
      .map(seatId => {
        const seat = seats.find(s => s.id === seatId);
        return seat ? seat.seatNumber : seatId;
      })
      .join(', ');
  };

  const getFBManagerName = () => {
    if (!allocation || !staff.length) return 'N/A';
    const manager = staff.find(s => s.id === allocation.fbManagerId);
    return manager ? manager.name : 'N/A';
  };

  const getPoolBeachAttendantNames = () => {
    if (!allocation || !staff.length || !allocation.poolBeachAttendantIds?.length) return [];
    return allocation.poolBeachAttendantIds
      .map(id => {
        const attendant = staff.find(s => s.id === id);
        return attendant ? attendant.name : null;
      })
      .filter(name => name !== null);
  };

  const getFBServerNames = () => {
    if (!allocation || !staff.length || !allocation.fbServerIds?.length) return [];
    return allocation.fbServerIds
      .map(id => {
        const server = staff.find(s => s.id === id);
        return server ? server.name : null;
      })
      .filter(name => name !== null);
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading allocation details...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  if (!allocation) {
    return (
      <UserLayout>
        <div className="text-center py-12">
          <p className="text-slate-600 mb-4">Allocation not found</p>
          <Button onClick={() => navigate('/user/allocation')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Allocations
          </Button>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/user/allocation')}
              className="flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Allocation Details</h1>
              <p className="text-slate-600 mt-1">Complete timeline and analytics</p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-lg border-2 font-semibold ${getStatusColor(allocation.status)}`}>
            {allocation.status}
          </div>
        </div>

        {/* Guest Information Card */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Guest Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-start space-x-3">
              <User className="w-5 h-5 text-blue-500 mt-1" />
              <div>
                <p className="text-sm text-slate-600">Guest Name</p>
                <p className="font-semibold text-slate-800">{allocation.guestName}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="w-5 h-5 text-purple-500 mt-1" />
              <div>
                <p className="text-sm text-slate-600">Room Number</p>
                <p className="font-semibold text-slate-800">{allocation.roomNumber}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Armchair className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="text-sm text-slate-600">Seats</p>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-800">{getSeatNumbers() || 'N/A'}</p>
                  {allocation.status !== 'Complete' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleOpenChangeSeats}
                      className="h-7 px-2 text-xs"
                      data-testid="change-seats-btn"
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Change
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <User className="w-5 h-5 text-orange-500 mt-1" />
              <div>
                <p className="text-sm text-slate-600">F&B Manager</p>
                <p className="font-semibold text-slate-800">{getFBManagerName()}</p>
              </div>
            </div>
            {allocation.guestCategory && (
              <div className="flex items-start space-x-3">
                <TrendingUp className="w-5 h-5 text-amber-500 mt-1" />
                <div>
                  <p className="text-sm text-slate-600">Category</p>
                  <p className="font-semibold text-amber-600">{allocation.guestCategory}</p>
                </div>
              </div>
            )}
            <div className="flex items-start space-x-3">
              <Calendar className="w-5 h-5 text-teal-500 mt-1" />
              <div>
                <p className="text-sm text-slate-600">Allocation Date</p>
                <p className="font-semibold text-slate-800">
                  {new Date(allocation.createdAt).toLocaleString('en-US', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Users className="w-5 h-5 text-indigo-500 mt-1" />
              <div>
                <p className="text-sm text-slate-600">Number of Adults</p>
                <p className="font-semibold text-slate-800">{allocation.numAdults || 0}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Baby className="w-5 h-5 text-pink-500 mt-1" />
              <div>
                <p className="text-sm text-slate-600">Number of Kids</p>
                <p className="font-semibold text-slate-800">{allocation.numKids || 0}</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Smartphone className="w-5 h-5 text-cyan-500 mt-1" />
              <div>
                <p className="text-sm text-slate-600">Assigned Devices</p>
                <div className="font-semibold text-slate-800">
                  {allocation.deviceIds && allocation.deviceIds.length > 0 
                    ? allocation.deviceIds.map((deviceUuid, index) => {
                        const device = devices.find(d => d.id === deviceUuid);
                        return (
                          <div key={deviceUuid}>
                            {device ? `${device.deviceId} (${deviceUuid})` : deviceUuid}
                          </div>
                        );
                      })
                    : 'No devices assigned'}
                </div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Lock className="w-5 h-5 text-amber-500 mt-1" />
              <div>
                <p className="text-sm text-slate-600">Ordering PIN</p>
                <p className="font-semibold text-slate-800">
                  {allocation.pinEnabled ? (
                    <span className="flex items-center gap-2">
                      <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded font-mono tracking-widest">
                        {allocation.pin || '****'}
                      </span>
                      <span className="text-xs text-green-600">Enabled</span>
                    </span>
                  ) : (
                    <span className="text-slate-400">Not enabled</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Staff Information */}
          {(getPoolBeachAttendantNames().length > 0 || getFBServerNames().length > 0) && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="text-md font-semibold text-slate-700 mb-4">Assigned Staff</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getPoolBeachAttendantNames().length > 0 && (
                  <div className="flex items-start space-x-3">
                    <User className="w-5 h-5 text-blue-500 mt-1" />
                    <div>
                      <p className="text-sm text-slate-600">Pool And Beach Attendant</p>
                      <div className="font-semibold text-slate-800">
                        {getPoolBeachAttendantNames().map((name, index) => (
                          <span key={index}>
                            {name}
                            {index < getPoolBeachAttendantNames().length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {getFBServerNames().length > 0 && (
                  <div className="flex items-start space-x-3">
                    <User className="w-5 h-5 text-green-500 mt-1" />
                    <div>
                      <p className="text-sm text-slate-600">Pool And Beach Server</p>
                      <div className="font-semibold text-slate-800">
                        {getFBServerNames().map((name, index) => (
                          <span key={index}>
                            {name}
                            {index < getFBServerNames().length - 1 && ', '}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Associated Guest Profiles */}
        {associatedGuestProfiles.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-teal-500" />
              Associated Guest Profiles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {associatedGuestProfiles.map((profile) => (
                <div
                  key={profile.id}
                  className="bg-slate-50 rounded-lg p-4 border border-slate-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div 
                      className="flex items-center space-x-3 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => navigate(`/user/guest-profile/${profile.id}`)}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {profile.firstName?.charAt(0)}{profile.lastName?.charAt(0) || ''}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800 hover:text-teal-600 transition-colors">
                          {profile.firstName} {profile.lastName || ''}
                        </h3>
                        <p className="text-xs text-slate-500">{profile.numVisits || 0} visits</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditModal(profile);
                      }}
                      className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {profile.allergies && (
                      <div className="flex items-start space-x-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                        <span className="text-slate-600">{profile.allergies}</span>
                      </div>
                    )}
                    {profile.preferences && (
                      <div className="flex items-start space-x-2">
                        <Heart className="w-4 h-4 text-pink-500 mt-0.5" />
                        <span className="text-slate-600">{profile.preferences}</span>
                      </div>
                    )}
                    {(profile.favouriteDrink || profile.favouriteFood) && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {profile.favouriteDrink && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                            🍹 {profile.favouriteDrink}
                          </span>
                        )}
                        {profile.favouriteFood && (
                          <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">
                            🍽️ {profile.favouriteFood}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Edit Guest Profile Modal */}
        {editingProfile && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-800">Edit Guest Profile</h2>
                <button
                  onClick={closeEditModal}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* First Name - Disabled */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">First Name</Label>
                  <Input
                    value={editFormData.firstName}
                    disabled
                    className="mt-1 bg-slate-100 cursor-not-allowed"
                  />
                </div>

                {/* Last Name - Disabled */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Last Name</Label>
                  <Input
                    value={editFormData.lastName}
                    disabled
                    className="mt-1 bg-slate-100 cursor-not-allowed"
                  />
                </div>

                {/* Phone */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Country Code</Label>
                    <Input
                      value={editFormData.countryCode}
                      onChange={(e) => handleEditFormChange('countryCode', e.target.value)}
                      placeholder="+1"
                      className="mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-sm font-medium text-slate-700">Phone Number</Label>
                    <Input
                      value={editFormData.phoneNumber}
                      onChange={(e) => handleEditFormChange('phoneNumber', e.target.value)}
                      placeholder="Phone number"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Date of Birth */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Date of Birth</Label>
                  <Input
                    type="date"
                    value={editFormData.dateOfBirth}
                    onChange={(e) => handleEditFormChange('dateOfBirth', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Nationality */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Nationality</Label>
                  <Input
                    value={editFormData.nationality}
                    onChange={(e) => handleEditFormChange('nationality', e.target.value)}
                    placeholder="Nationality"
                    className="mt-1"
                  />
                </div>

                {/* Allergies */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Allergies</Label>
                  <Input
                    value={editFormData.allergies}
                    onChange={(e) => handleEditFormChange('allergies', e.target.value)}
                    placeholder="Any allergies"
                    className="mt-1"
                  />
                </div>

                {/* Preferences */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Preferences</Label>
                  <Input
                    value={editFormData.preferences}
                    onChange={(e) => handleEditFormChange('preferences', e.target.value)}
                    placeholder="Guest preferences"
                    className="mt-1"
                  />
                </div>

                {/* Favourite Drink */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Favourite Drink</Label>
                  <Input
                    value={editFormData.favouriteDrink}
                    onChange={(e) => handleEditFormChange('favouriteDrink', e.target.value)}
                    placeholder="Favourite drink"
                    className="mt-1"
                  />
                </div>

                {/* Favourite Food */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Favourite Food</Label>
                  <Input
                    value={editFormData.favouriteFood}
                    onChange={(e) => handleEditFormChange('favouriteFood', e.target.value)}
                    placeholder="Favourite food"
                    className="mt-1"
                  />
                </div>

                {/* Number of Visits */}
                <div>
                  <Label className="text-sm font-medium text-slate-700">Number of Visits</Label>
                  <Input
                    type="number"
                    value={editFormData.numVisits}
                    onChange={(e) => handleEditFormChange('numVisits', parseInt(e.target.value) || 0)}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={closeEditModal}
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveGuestProfile}
                  disabled={savingProfile}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {savingProfile ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Active Cart & Orders Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Active Cart Section */}
          <div className="bg-white rounded-xl shadow-md p-6" data-testid="active-cart-section">
            <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center">
              <ShoppingCart className="w-5 h-5 mr-2 text-amber-500" />
              Active Cart
            </h2>
            {activeCart && activeCart.items?.length > 0 ? (
              <div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left">
                        <th className="pb-2 font-medium text-slate-600">Item</th>
                        <th className="pb-2 font-medium text-slate-600 text-center">Qty</th>
                        <th className="pb-2 font-medium text-slate-600 text-right">Price</th>
                        <th className="pb-2 font-medium text-slate-600 text-right">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {activeCart.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className="py-2 text-slate-800">{item.menuItemName}</td>
                          <td className="py-2 text-slate-700 text-center">{item.quantity}</td>
                          <td className="py-2 text-slate-600 text-right">${item.price?.toFixed(2)}</td>
                          <td className="py-2 text-slate-800 font-medium text-right">${(item.price * item.quantity).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3 pt-3 border-t border-slate-200 flex justify-end">
                  <span className="text-base font-semibold text-slate-800">Total: ${activeCart.totalAmount?.toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400">
                <ShoppingCart className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No active cart</p>
              </div>
            )}
          </div>

          {/* Orders Placed Section */}
          <div className="bg-white rounded-xl shadow-md p-6" data-testid="orders-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center">
                <ClipboardList className="w-5 h-5 mr-2 text-green-500" />
                Orders Placed ({orders.length})
              </h2>
              {orders.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/user/allocation/${allocationId}/billing`)}
                  className="text-teal-700 border-teal-300 hover:bg-teal-50"
                  data-testid="view-bill-btn"
                >
                  <Receipt className="w-4 h-4 mr-1.5" />
                  View Bill
                </Button>
              )}
            </div>
            {orders.length > 0 ? (
              <div className="space-y-4 max-h-[400px] overflow-y-auto">
                {orders.map((order) => (
                  <div key={order.id} className="border border-slate-200 rounded-lg p-4 cursor-pointer hover:border-teal-300 hover:shadow-sm transition-all" onClick={() => navigate(`/user/orders/${order.id}`)}>
                    <div className="flex items-center justify-between mb-2">
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/user/orders/${order.id}`); }} className="text-xs font-mono font-bold text-teal-600 hover:underline">#{order.id}</button>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${(order.orderType === 'Bar') ? 'bg-cyan-100 text-cyan-700' : 'bg-orange-100 text-orange-700'}`}>
                        {order.orderType || 'Kitchen'}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span className={`inline-block text-xs px-2.5 py-1 rounded-full font-semibold ${
                        order.status === 'Order Placed' ? 'bg-blue-100 text-blue-700' :
                        order.status === 'Order Accepted' ? 'bg-indigo-100 text-indigo-700' :
                        order.status === 'In Preparation' ? 'bg-amber-100 text-amber-700' :
                        order.status === 'Ready to Pick' ? 'bg-purple-100 text-purple-700' :
                        order.status === 'Picked Up' ? 'bg-cyan-100 text-cyan-700' :
                        order.status === 'Delivered' ? 'bg-green-100 text-green-700' :
                        order.status === 'Cancelled by Customer' ? 'bg-red-100 text-red-700' :
                        order.status === 'Cancelled By Staff' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {order.status || 'Unknown'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-slate-500">
                        {new Date(order.createdAt).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                      <span className="font-semibold text-slate-800">${order.totalAmount?.toFixed(2)}</span>
                    </div>
                    <div className="space-y-1">
                      {order.items?.map((item, idx) => (
                        <div key={idx}>
                          <div className="flex justify-between text-sm text-slate-600">
                            <span>{item.menuItemName} x{item.quantity}</span>
                            <span>${(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                          {item.notes && (
                            <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-0.5 italic" data-testid={`allocation-order-remark-${order.id}-${idx}`}>
                              {item.notes}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-slate-400">
                <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No orders placed yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-md p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <Timer className="w-8 h-8 opacity-80" />
              </div>
              <p className="text-sm opacity-90 mb-1">Total Time</p>
              <p className="text-3xl font-bold">
                {analytics.totalTime.hours}h {analytics.totalTime.minutes}m {analytics.totalTime.seconds}s
              </p>
            </div>

            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-md p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <Phone className="w-8 h-8 opacity-80" />
              </div>
              <p className="text-sm opacity-90 mb-1">Calling Count</p>
              <p className="text-3xl font-bold">{analytics.callingCount}</p>
              <p className="text-xs opacity-75 mt-1">times pressed</p>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-md p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-8 h-8 opacity-80" />
              </div>
              <p className="text-sm opacity-90 mb-1">Avg Calling Time</p>
              <p className="text-3xl font-bold">{analytics.avgCallingTime.minutes}m {analytics.avgCallingTime.seconds}s</p>
              <p className="text-xs opacity-75 mt-1">per call</p>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-md p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <Activity className="w-8 h-8 opacity-80" />
              </div>
              <p className="text-sm opacity-90 mb-1">Active Time</p>
              <p className="text-3xl font-bold">{analytics.activeTime.minutes}m {analytics.activeTime.seconds}s</p>
              <p className="text-xs opacity-75 mt-1">in Active status</p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl shadow-md p-6 text-white">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 opacity-80" />
              </div>
              <p className="text-sm opacity-90 mb-1">Status Changes</p>
              <p className="text-3xl font-bold">{analytics.statusChangeCount}</p>
              <p className="text-xs opacity-75 mt-1">transitions</p>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-6 flex items-center">
            <Activity className="w-5 h-5 mr-2 text-blue-500" />
            Event Timeline
          </h2>
          
          {allocation.events && allocation.events.length > 0 ? (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200"></div>
              
              <div className="space-y-2">
                {(() => {
                  // Process events to club calling on/off pairs
                  const events = [...allocation.events].reverse();
                  const processedEvents = [];
                  const callingRecords = allocation.callingRecords || [];
                  
                  for (let i = 0; i < events.length; i++) {
                    const event = events[i];
                    
                    // Check if this is a Calling Off event with a paired record
                    if (event.eventType === 'Calling Off' && event.callingRecordId) {
                      // Find the corresponding Calling On event
                      const record = callingRecords.find(r => r.id === event.callingRecordId);
                      if (record) {
                        // Create a clubbed event
                        processedEvents.push({
                          ...event,
                          isClubbed: true,
                          callingRecord: record
                        });
                        continue;
                      }
                    }
                    
                    // Skip Calling On events that are part of a completed pair (they'll be shown in clubbed view)
                    if (event.eventType === 'Calling On') {
                      const recordId = callingRecords.find(r => 
                        r.startTime === event.timestamp && r.endTime !== null
                      )?.id;
                      if (recordId) {
                        // This Calling On is part of a completed pair, skip it
                        continue;
                      }
                    }
                    
                    processedEvents.push(event);
                  }

                  // Helper function to get event time
                  const getEventTime = (event) => {
                    if (event.isClubbed && event.callingRecord?.startTime) {
                      return new Date(event.callingRecord.startTime);
                    }
                    return new Date(event.timestamp);
                  };

                  // Helper function to format time gap
                  const formatTimeGap = (ms) => {
                    const seconds = Math.floor(ms / 1000);
                    const minutes = Math.floor(seconds / 60);
                    const hours = Math.floor(minutes / 60);
                    const days = Math.floor(hours / 24);

                    if (days > 0) {
                      return `${days}d ${hours % 24}h`;
                    } else if (hours > 0) {
                      return `${hours}h ${minutes % 60}m`;
                    } else if (minutes > 0) {
                      return `${minutes}m ${seconds % 60}s`;
                    } else {
                      return `${seconds}s`;
                    }
                  };
                  
                  return processedEvents.map((event, index) => {
                    // Calculate time gap with previous event
                    let timeGap = null;
                    if (index > 0) {
                      const currentTime = getEventTime(event);
                      const prevTime = getEventTime(processedEvents[index - 1]);
                      const gapMs = Math.abs(prevTime - currentTime);
                      if (gapMs > 0) {
                        timeGap = formatTimeGap(gapMs);
                      }
                    }

                    return (
                      <React.Fragment key={index}>
                        {/* Time Gap Indicator */}
                        {timeGap && (
                          <div className="relative pl-14 py-1">
                            <div className="absolute left-[22px] top-1/2 transform -translate-y-1/2 w-4 h-4 bg-slate-100 border border-slate-300 rounded-full flex items-center justify-center">
                              <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                            </div>
                            <div className="flex items-center">
                              <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                                ↕ {timeGap}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Event Card */}
                        <div className="relative pl-14">
                          {/* Icon */}
                          <div className={`absolute left-0 top-0 w-12 h-12 bg-white border-2 rounded-full flex items-center justify-center shadow-sm ${
                            event.isClubbed 
                              ? event.callingRecord?.callingType === 'Proactive Checkin'
                                ? 'border-green-400'
                                : 'border-amber-400'
                              : 'border-slate-200'
                          }`}>
                            {getEventIcon(event.eventType)}
                          </div>
                          
                          {/* Content */}
                          {event.isClubbed ? (
                            <div className={`rounded-lg p-4 border-2 ${
                              event.callingRecord?.callingType === 'Proactive Checkin'
                                ? 'bg-green-50 border-green-200'
                                : 'bg-amber-50 border-amber-200'
                            }`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-slate-800">Call Session</h3>
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                    event.callingRecord?.callingType === 'Proactive Checkin'
                                      ? 'bg-green-200 text-green-800'
                                      : 'bg-amber-200 text-amber-800'
                                  }`}>
                                    {event.callingRecord?.callingType}
                                  </span>
                                  {event.callingRecord?.callingMode === 'Calling for Checkout' && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-200 text-purple-800 font-medium">
                                      Checkout
                                    </span>
                                  )}
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-4 mb-3">
                                <div className="text-sm">
                                  <span className="text-slate-500">Started:</span>
                                  <span className="ml-2 text-slate-700">{formatTime(event.callingRecord?.startTime)}</span>
                                </div>
                                <div className="text-sm">
                                  <span className="text-slate-500">Ended:</span>
                                  <span className="ml-2 text-slate-700">{formatTime(event.callingRecord?.endTime)}</span>
                                </div>
                              </div>
                              
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-slate-500" />
                                  <span className="text-sm font-medium text-slate-700">
                                    Duration: {event.callingRecord?.durationSeconds < 60 
                                      ? `${event.callingRecord?.durationSeconds?.toFixed(1)}s`
                                      : `${Math.floor(event.callingRecord?.durationSeconds / 60)}m ${Math.round(event.callingRecord?.durationSeconds % 60)}s`
                                    }
                                  </span>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded ${
                                  event.callingRecord?.callingType === 'Proactive Checkin'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {event.callingRecord?.callingType === 'Proactive Checkin' 
                                    ? '< 5 seconds' 
                                    : '≥ 5 seconds'}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                              <div className="flex items-center justify-between mb-2">
                                <h3 className="font-semibold text-slate-800">{event.eventType}</h3>
                                <span className="text-xs text-slate-500">{formatTime(event.timestamp)}</span>
                              </div>
                              <p className="text-sm text-slate-600 mb-2">{event.description}</p>
                              {event.oldValue && event.newValue && (
                                <div className="flex items-center space-x-2 text-xs">
                                  <span className="bg-slate-200 px-2 py-1 rounded text-slate-700">
                                    {event.oldValue}
                                  </span>
                                  <span className="text-slate-400">→</span>
                                  <span className="bg-blue-100 px-2 py-1 rounded text-blue-700 font-medium">
                                    {event.newValue}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No events recorded yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Change Seats Modal */}
      <Dialog open={changeSeatsOpen} onOpenChange={setChangeSeatsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change Seats</DialogTitle>
            <DialogDescription>
              Select new seats for this allocation. Red seats are already allocated to other guests.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="text-slate-700 font-medium mb-3 block">
              Select Seats * ({selectedSeatIds.length} selected)
            </Label>
            
            <div className="border border-slate-300 rounded-lg p-4 max-h-64 overflow-y-auto">
              {seats.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No seats available</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {seats.filter(seat => seat.status !== 'Blocked').map((seat) => {
                    const isAllocatedByOthers = allocatedSeats.includes(seat.id);
                    const isSelected = selectedSeatIds.includes(seat.id);
                    const seatType = seatTypes.find(st => st.id === seat.seatTypeId);
                    
                    return (
                      <button
                        key={seat.id}
                        type="button"
                        onClick={() => !isAllocatedByOthers && handleSeatToggle(seat.id)}
                        disabled={isAllocatedByOthers}
                        className={`flex flex-col items-center justify-center p-2 rounded-lg border-2 transition-all min-h-[80px] ${
                          isSelected
                            ? 'bg-blue-500 border-blue-500 text-white'
                            : isAllocatedByOthers
                            ? 'bg-red-100 border-red-300 text-red-400 cursor-not-allowed'
                            : 'bg-white border-slate-300 text-slate-700 hover:border-blue-300'
                        }`}
                        data-testid={`seat-${seat.seatNumber}`}
                      >
                        {seatType && seatType.icon ? (
                          <img 
                            src={seatType.icon} 
                            alt={seatType.name}
                            className={`w-8 h-8 object-contain mb-1 ${
                              isSelected ? 'brightness-0 invert' : isAllocatedByOthers ? 'opacity-50' : ''
                            }`}
                          />
                        ) : (
                          <Armchair className="w-6 h-6 mb-1" />
                        )}
                        <span className="text-xs font-semibold">{seat.seatNumber}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-4 text-xs text-slate-600 mt-3">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span>Selected</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-red-100 border border-red-300 rounded"></div>
                <span>Already Allocated</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-white border border-slate-300 rounded"></div>
                <span>Available</span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeSeatsOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => setSeatConfirmOpen(true)} 
              disabled={selectedSeatIds.length === 0}
              data-testid="save-seats-btn"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seat Change Confirmation Dialog */}
      <Dialog open={seatConfirmOpen} onOpenChange={setSeatConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirm Seat Change</DialogTitle>
            <DialogDescription>
              Please review the seat change before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">Current Seats</p>
              <p className="font-semibold text-red-700">
                {(allocation?.seatIds || []).map(id => seats.find(s => s.id === id)?.seatNumber).filter(Boolean).join(', ') || 'None'}
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <p className="text-xs text-slate-500 mb-1">New Seats</p>
              <p className="font-semibold text-green-700">
                {selectedSeatIds.map(id => seats.find(s => s.id === id)?.seatNumber).filter(Boolean).join(', ') || 'None'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSeatConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={() => { setSeatConfirmOpen(false); handleSaveSeats(); }}
              disabled={savingSeats}
              data-testid="confirm-seat-change-btn"
            >
              {savingSeats ? 'Saving...' : 'Confirm Change'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UserLayout>
  );
};
